import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { euro, formatDate } from "@/lib/constants";
import { Plus, Plane, CalendarClock, History, MapPin, ArrowRight } from "lucide-react";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</div>
        <Icon size={18} className="text-[#E30613]" />
      </div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
    </div>
  );
}

function TripRow({ trip, onClick }) {
  return (
    <button
      data-testid={`trip-row-${trip.id}`}
      onClick={onClick}
      className="w-full text-left bg-white border border-slate-200 rounded-lg p-4 shadow-sm hover:border-[#E30613] transition-colors flex items-center justify-between gap-4"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-slate-900 font-semibold">
          <MapPin size={16} className="text-[#E30613] shrink-0" />
          <span className="truncate">{trip.destinazione || "Senza destinazione"}</span>
        </div>
        <div className="text-sm text-slate-500 mt-1 truncate">
          {trip.dipendente} · {formatDate(trip.data_partenza)} → {formatDate(trip.data_rientro)}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <StatusBadge stato={trip.stato} />
        <ArrowRight size={16} className="text-slate-400" />
      </div>
    </button>
  );
}

export default function Dashboard() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, activeCompany } = useAuth();
  const navigate = useNavigate();
  const effectiveCompany = activeCompany || user?.active_company || user?.company;

  useEffect(() => {
    setLoading(true);
    api.get("/trips").then((r) => setTrips(r.data)).catch((e) => apiError(e)).finally(() => setLoading(false));
  }, [effectiveCompany]);

  const today = new Date().toISOString().slice(0, 10);
  const future = trips.filter((t) => (t.data_partenza || "") >= today && t.stato !== "annullata");
  const recenti = [...trips].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 5);
  const daApprovare = trips.filter((t) => t.stato === "in_approvazione");

  return (
    <Layout>
      <div className="mb-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Ciao, {user?.name?.split(" ")[0]}</h1>
          <p className="text-slate-500 mt-1">Ecco una panoramica delle trasferte.</p>
        </div>
        <Button data-testid="btn-new-trip" onClick={() => navigate("/nuova-trasferta")} className="w-full justify-center bg-[#E30613] font-semibold hover:bg-[#C50510] sm:w-auto">
          <Plus size={18} className="mr-1.5" /> Nuova trasferta
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Trasferte totali" value={trips.length} icon={Plane} testid="stat-total" />
        <StatCard label="Future" value={future.length} icon={CalendarClock} testid="stat-future" />
        <StatCard label="Recenti" value={recenti.length} icon={History} testid="stat-recent" />
        {(user?.role === "responsabile" || user?.role === "amministratore") && (
          <StatCard label="Da approvare" value={daApprovare.length} icon={Plane} testid="stat-approve" />
        )}
      </div>

      {loading ? (
        <div className="text-slate-500">Caricamento...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section>
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CalendarClock size={20} className="text-[#E30613]" /> Trasferte future
            </h2>
            <div className="space-y-3">
              {future.length === 0 && <p className="text-sm text-slate-400">Nessuna trasferta futura.</p>}
              {future.slice(0, 6).map((t) => (
                <TripRow key={t.id} trip={t} onClick={() => navigate(`/trasferte/${t.id}`)} />
              ))}
            </div>
          </section>
          <section>
            <h2 className="font-display text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <History size={20} className="text-[#E30613]" /> Trasferte recenti
            </h2>
            <div className="space-y-3">
              {recenti.length === 0 && <p className="text-sm text-slate-400">Nessuna trasferta.</p>}
              {recenti.map((t) => (
                <TripRow key={t.id} trip={t} onClick={() => navigate(`/trasferte/${t.id}`)} />
              ))}
            </div>
            <Link to="/trasferte" data-testid="see-all-trips" className="inline-flex items-center gap-1 text-sm text-[#E30613] font-medium mt-4 hover:underline">
              Vedi tutte le trasferte <ArrowRight size={14} />
            </Link>
          </section>
        </div>
      )}
    </Layout>
  );
}
